// src/components/audit/ImportCsvModal.jsx
import React, { useState } from "react";
import { Modal, Form } from "react-bootstrap";
import axios from "axios";
import "./ImportCsvModal.css";

export default function ImportCsvModal({
  show,
  onHide,
  country,
  year,
  onImported,
  onToast
}) {
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("upsert");
  const [submitting, setSubmitting] = useState(false);
  const [report, setReport] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      onToast?.("Please select a CSV file", "warning");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("country", country);
    formData.append("year", year);
    formData.append("mode", mode);

    try {
      setSubmitting(true);
      setReport(null);

      const res = await axios.post("/api/audit_import.php", formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data?.success) {
        const data = res.data.data;
        setReport(data);
        onToast?.(
          `Import complete — ${data.imported} new, ${data.updated} updated, ${data.skipped} skipped`,
          "success"
        );
        onImported?.();
      } else {
        onToast?.(res.data?.error || "Import failed", "danger");
      }
    } catch (err) {
      const msg = err?.response?.data?.error || err.message || "Network error";
      onToast?.(msg, "danger");
    } finally {
      setSubmitting(false);
    }
  };

  const resetAndClose = () => {
    setFile(null);
    setMode("upsert");
    setReport(null);
    onHide?.();
  };

  return (
    <Modal
      show={show}
      onHide={submitting ? undefined : resetAndClose}
      centered
      size="lg"
      backdrop="static"
      className="import-csv-modal"
    >
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton={!submitting}>
          <Modal.Title>Import Audit Findings (CSV)</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="mb-3">
            <div className="text-muted mb-2">
              Country: <strong>{country}</strong> — Year: <strong>{year}</strong>
            </div>

            <Form.Group>
              <Form.Label>CSV File</Form.Label>
              <Form.Control
                type="file"
                accept=".csv"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                disabled={submitting}
              />
              <div className="form-text">Expected delimiter: semicolon (;)</div>
            </Form.Group>
          </div>

          <div className="mb-3">
            <Form.Group>
              <Form.Label>Mode</Form.Label>
              <Form.Select
                value={mode}
                onChange={(e) => setMode(e.target.value)}
                disabled={submitting}
              >
                <option value="upsert">Upsert existing (by Finding ID)</option>
                <option value="insert-only">Insert only — skip duplicates</option>
              </Form.Select>
            </Form.Group>
          </div>

          {report && (
            <div className="import-report-box">
              <div>
                Imported: <strong>{report.imported}</strong>
              </div>
              <div>
                Updated: <strong>{report.updated}</strong>
              </div>
              <div>
                Skipped: <strong>{report.skipped}</strong>
              </div>

              {report.errors?.length > 0 && (
                <details className="mt-2">
                  <summary>Errors ({report.errors.length})</summary>
                  <pre>
                    {report.errors
                      .map(
                        (e) =>
                          `Row ${e.row} [${e.finding_id}]: ${e.error}`
                      )
                      .join("\n")}
                  </pre>
                </details>
              )}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={resetAndClose}
            disabled={submitting}
          >
            Cancel
          </button>

          <button
            type="submit"
            className="btn btn-success"
            disabled={submitting || !file}
          >
            {submitting ? (
              <>
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                />{" "}
                Importing…
              </>
            ) : (
              "Start Import"
            )}
          </button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
